import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { KickstartService } from '../../../../core/kickstart/kickstart.service';
import { KsClientContextEntry } from '../../../../core/kickstart/kickstart.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { uploadErrorMessage } from '../../shared/doc-drop/doc-drop';

/**
 * The *Tell us about your business* starter prompts (DOC-72 UI-DA-72-08, with
 * the Product Owner's 2026-10-02 correction restoring *Other funding* —
 * informal context, never the formal funder question, which stays with Ops).
 */
export const CONTEXT_PROMPTS = [
  'What we do',
  'Our biggest clients',
  'Busy and quiet months',
  'Orders coming up',
  'Why we need the funding',
  'Other funding',
  'Something in the documents worth explaining',
] as const;

/**
 * Press a prompt: add its labelled line, or — if that line is still untouched —
 * take it back out (the PO's design).
 */
export function togglePrompt(text: string, label: string): string {
  const line = `${label}: `;
  const lines = text.split('\n');
  const at = lines.findIndex((l) => l === line || l === `${label}:`);
  if (at >= 0) {
    lines.splice(at, 1);
    return lines.join('\n');
  }
  if (!text) return line;
  return text.endsWith('\n') ? text + line : `${text}\n${line}`;
}

/**
 * *Tell us about your business* (DOC-72 §4A) — on the application form and,
 * after submission, on the application page until an offer is issued (DOC-73
 * UI-DA-73-04). Optional; never blocks.
 *
 * 🚨 Client context — never evidence: the back end records each entry with
 * who and when, and no assessment reads it. Append-only: every addition is a
 * new entry, and nothing here edits or removes one.
 */
@Component({
  selector: 'app-ks-context-block',
  imports: [
    DatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    IconComponent,
  ],
  templateUrl: './ks-context-block.html',
  styleUrl: './ks-context-block.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsContextBlockComponent {
  private readonly kickstart = inject(KickstartService);

  readonly shortId = input.required<string>();
  /** The entries as last read from the back end. */
  readonly initialEntries = input<KsClientContextEntry[]>([]);
  /** Whether the block still takes entries — the back end's `client_context_open`. */
  readonly open = input(true);

  /** The list after each addition, for a host that shows it elsewhere (the review step). */
  readonly entriesChange = output<KsClientContextEntry[]>();
  /** True while a note or document is saving — a host waits on it before moving on. */
  readonly busyChange = output<boolean>();

  protected readonly CONTEXT_PROMPTS = CONTEXT_PROMPTS;
  protected readonly text = signal('');
  protected readonly entries = linkedSignal(() => this.initialEntries());
  protected readonly uploading = signal(0);
  protected readonly error = signal<string | null>(null);
  private destroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => (this.destroyed = true));
  }

  protected pressPrompt(label: string): void {
    this.text.update((text) => togglePrompt(text, label));
  }

  /**
   * Save what is typed as one entry. Append-only: the box clears, the entry
   * stays. `done` runs once it is saved — never on a refusal, which is shown.
   */
  addNote(done?: () => void): void {
    const text = this.text().trim();
    if (!text) return;
    this.save({ text }, () => {
      this.text.set('');
      done?.();
    });
  }

  /** Whether something is typed but not yet added. */
  hasPendingText(): boolean {
    return this.text().trim().length > 0;
  }

  protected onFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    for (const file of files) this.save({ file });
  }

  /**
   * Deliberately not cut off when the block is destroyed: the create form's
   * steps are `@if` blocks, and an entry the business asked to add must not be
   * aborted mid-request by a step change. One HTTP call — nothing leaks.
   */
  private save(entry: { text?: string; file?: File }, done?: () => void): void {
    this.setUploading(1);
    this.error.set(null);
    this.kickstart.addContext(this.shortId(), entry).subscribe({
      next: (saved) => {
        this.entries.update((list) => [...list, saved]);
        this.setUploading(-1);
        if (!this.destroyed) this.entriesChange.emit(this.entries());
        done?.();
      },
      error: (err: unknown) => {
        this.setUploading(-1);
        // The server says why it cannot take it — a file it refuses, or the
        // block closed because an offer was issued meanwhile (409).
        this.error.set(uploadErrorMessage(err));
      },
    });
  }

  private setUploading(delta: 1 | -1): void {
    this.uploading.update((n) => n + delta);
    if (!this.destroyed) this.busyChange.emit(this.uploading() > 0);
  }
}
